// -----------------------------------------------------------------------
// <copyright file="LinkWithContextMenu.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import {
  classNamesFunction,
  ContextualMenu,
  IContextualMenuItem,
  ILinkProps,
  ILinkStyles,
  IProcessedStyleSet,
  Link,
  mergeStyles,
  styled,
} from '@fluentui/react';
import * as React from 'react';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ISelfHelpAlchemyState } from '../../../web-legacy/models/reduxState';
import { isGetHelpSelector } from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { selfHelpAlchemyStateSelector } from '../../../web-legacy/packages/app/selectors/selfHelpSelectors';
import { getHelpService } from '../../../web-legacy/packages/contactUs/services/getHelpServices';
import { GetHelpLoggingRequest } from '../../models/getHelp/GetHelpLoggingRequest';
import { getString } from '../../utils/stringLoader';
import { addGenevaLog } from '../../utils/statelessUtil';
import { postAlchemyFeedbackAsync } from '../../common/apis/contactUs.api';

const copyTextToClipboard = (text: string) => {
  // Create a temporary element to append to DOM, put the text into it, select and copy, then remove the element
  const tempElement: HTMLTextAreaElement = document.createElement('textarea');
  tempElement.value = text;
  tempElement.readOnly = true;
  tempElement.style.position = 'absolute';
  tempElement.style.top = '-50px';
  tempElement.style.width = '100%';
  document.body.appendChild(tempElement);
  tempElement.select();
  document.execCommand('copy');
  document.body.removeChild(tempElement);
};

interface ILinkWithContextMenuProps extends ILinkProps {
  url: string;
  text: string;
  ariaLabel?: string;
  eventMessage?: GetHelpLoggingRequest;
  jsllMessage?: string;
  sendAlchemyFeedback?: boolean;
  dataBiProps?: object;
  contextUrl?: string;
  onClick?: () => void;
  id?: string;
  role?: string;
  sendAlchemyGPTFeedback: boolean;
}

const getClassNames = classNamesFunction<ILinkProps, ILinkStyles>();

const LinkWithContextMenuBase = (props: ILinkWithContextMenuProps): JSX.Element => {
  const alchemyResult: ISelfHelpAlchemyState = useSelector(selfHelpAlchemyStateSelector);
  const [mouseEvent, setMouseEvent] = useState(null);
  const isGetHelp: boolean = useSelector(isGetHelpSelector);
  const [isContextualMenuVisible, setContextualMenuVisibility] = useState(false);

  const classNames: IProcessedStyleSet<ILinkStyles> = getClassNames(props.styles!, {
    theme: props.theme!,
    className: props.className!,
  });

  const showContextualMenu = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    event.preventDefault();
    setMouseEvent(event.nativeEvent);
    setContextualMenuVisibility(true);
  };

  const hideContextualMenu = (): void => {
    setContextualMenuVisibility(false);
  };

  const menuItems: IContextualMenuItem[] = [
    {
      key: 'copyLink',
      text: getString('GetHelp_CopyLinkAddress'),
      onClick: () => copyTextToClipboard(props.contextUrl || props.url),
    },
    {
      key: 'openInBrowser',
      text: getString('GetHelp_OpenInBrowser'),
      // eslint-disable-next-line security/detect-non-literal-fs-filename
      onClick: () => window.open(props.contextUrl || props.url, '_blank'),
    },
  ];

  const logReadArticleClick = async () => {
    if (props.onClick && typeof props.onClick === 'function') {
      props.onClick();
    }
    if (isGetHelp) {
      if (!!props.eventMessage) {
        getHelpService.logEventAsync(props.eventMessage);
      }
      if (props.sendAlchemyFeedback) {
        postAlchemyFeedbackAsync({
          ClickedBcqrLink: props.url,
          IsBcqrRetrieved: alchemyResult.areSolutionsPresent,
          IsInsightRetrieved: alchemyResult.areInsightsPresent,
        });
      }
    }
  };

  const logAlchemyGPTFeedbackClick = async () => {
    if (props.onClick && typeof props.onClick === 'function') {
      props.onClick();
    }
    const messageObj = {
      alchemyGPTFeedbackClick: props.text
    }
    const logHandlerTags = {
      content: {
        contentId: 'alchemyGPTFeedbackClick',
        contentName: 'alchemyGPTFeedbackClick',
        message: JSON.stringify(messageObj),
        url: `${window.location.href}`,
      },
      behavior: 'OTHER',
    };
    addGenevaLog(logHandlerTags);
  }

  const dataBiProps: object = props.dataBiProps || {};

  return (
    <>
      <div className={mergeStyles(props.className, classNames.root)}>
        {!!props.jsllMessage ? (
          <Link
            aria-label={!!props.ariaLabel ? props.ariaLabel : props.text}
            href={props.url}
            target="_blank"
            onClick={props.sendAlchemyGPTFeedback ? logAlchemyGPTFeedbackClick : logReadArticleClick}
            onContextMenu={showContextualMenu}
            data-m={props.jsllMessage}
            {...dataBiProps}
            {... (!!props.id ? { id: props.id } : {})}
            {... (!!props.role ? { role: props.role } : {})}
          >
            {props.text}
          </Link>
        ) : (
          <Link
            aria-label={!!props.ariaLabel ? props.ariaLabel : props.text}
            href={props.url}
            target="_blank"
            onClick={logReadArticleClick}
            onContextMenu={showContextualMenu}
            {...dataBiProps}
            {... (!!props.id ? { id: props.id } : {})}
            {... (!!props.role ? { role: props.role } : {})}
          >
            {props.text}
          </Link>
        )}
      </div>
      {isContextualMenuVisible && isGetHelp && (
        <ContextualMenu
          items={menuItems}
          target={mouseEvent}
          onItemClick={hideContextualMenu}
          onDismiss={hideContextualMenu}
        />
      )}
    </>
  );
};

export const LinkWithContextMenu = styled(
  LinkWithContextMenuBase,
  (_props: ILinkProps): ILinkStyles => ({
    root: {},
  })
);
